import { Trans, useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import STRING_KEYS from '../../../../language/keys';
import { MATCH_STATUSES } from '../../../../consts';
import {
  ContactedBarIcon,
  Container,
  LikedBarIcon,
  PeopleMatchedBarIcon,
  RepliedBarIcon,
  StyledFunnelBar,
} from './position-performance-funnel.styled';
import { PositionPerformanceFunnelProps } from './position-performance-funnel';

const FUNNEL_GRAPH_START_POSITION = 0.42;

const PositionPerformanceFunnel = ({ positionStatistics }: PositionPerformanceFunnelProps): JSX.Element => {
  const { t: translate } = useTranslation();
  const {
    reviewedMatchesCount,
    toReviewMatchesCount,
    engagedMatchesCount,
    engagedMatchesRatio,
    contactedMatchesRatio,
    repliedConversationsRatio,
  } = useMemo(() => {
    const toReviewMatches = positionStatistics.engagementStatusStatistics.find((statistics) => statistics.name === MATCH_STATUSES.TO_REVIEW)?.amount || 0;
    const engagedMatches = positionStatistics.engagementStatusStatistics.find((statistics) => statistics.name === MATCH_STATUSES.ENGAGED)?.amount || 0;
    const declinedMatches = positionStatistics.engagementStatusStatistics.find((statistics) => statistics.name === MATCH_STATUSES.DECLINED)?.amount || 0;
    const reviewedMatches = engagedMatches + declinedMatches;

    return {
      reviewedMatchesCount: reviewedMatches,
      toReviewMatchesCount: toReviewMatches,
      engagedMatchesCount: engagedMatches,
      engagedMatchesRatio: engagedMatches / reviewedMatches,
      contactedMatchesRatio: positionStatistics.contactedMatches / reviewedMatches,
      repliedConversationsRatio: positionStatistics.repliedConversations / reviewedMatches,
    };
  }, [positionStatistics]);

  const createGraphPoint = (value: number) => {
    if (!reviewedMatchesCount) {
      return 1;
    }

    return 1 - (value * (1 - FUNNEL_GRAPH_START_POSITION));
  };

  const renderPeopleMatchedBar = () => {
    return (
      <StyledFunnelBar
        title={ translate(STRING_KEYS.POSITION_OVERVIEW_PAGE.POSITION_PERFORMANCE_FUNNEL.PEOPLE_MATCHED.TITLE) }
        subtitle={ toReviewMatchesCount ?
          translate(STRING_KEYS.POSITION_OVERVIEW_PAGE.POSITION_PERFORMANCE_FUNNEL.PEOPLE_MATCHED.SUBTITLE, {
            pendingReviewMatches: toReviewMatchesCount,
          }) : undefined }
        Icon={ PeopleMatchedBarIcon }
        count={ reviewedMatchesCount }
        infoText={ (
          <Trans
            i18nKey={ STRING_KEYS.POSITION_OVERVIEW_PAGE.POSITION_PERFORMANCE_FUNNEL.PEOPLE_MATCHED.INFO_TEXT }
            values={ { companiesCount: positionStatistics.matchesTalentsCompaniesCardinality } }
          />
        ) }
        start={ reviewedMatchesCount ? FUNNEL_GRAPH_START_POSITION : 1 }
        end={ createGraphPoint(engagedMatchesRatio) }
      />
    );
  };

  const renderLikedBar = () => {
    return (
      <StyledFunnelBar
        title={ translate(STRING_KEYS.POSITION_OVERVIEW_PAGE.POSITION_PERFORMANCE_FUNNEL.LIKED.TITLE) }
        Icon={ LikedBarIcon }
        count={ engagedMatchesCount }
        infoText={ positionStatistics.acceptanceRate == null ? undefined : (
          <Trans
            i18nKey={ STRING_KEYS.POSITION_OVERVIEW_PAGE.POSITION_PERFORMANCE_FUNNEL.LIKED.INFO_TEXT }
            values={ { acceptanceRate: Math.trunc(positionStatistics.acceptanceRate * 100) } }
          />
        ) }
        start={ createGraphPoint(engagedMatchesRatio) }
        end={ createGraphPoint(contactedMatchesRatio) }
      />
    );
  };

  const renderContactedBar = () => {
    return (
      <StyledFunnelBar
        title={ translate(STRING_KEYS.POSITION_OVERVIEW_PAGE.POSITION_PERFORMANCE_FUNNEL.CONTACTED.TITLE) }
        Icon={ ContactedBarIcon }
        count={ positionStatistics.contactedMatches }
        infoText={ positionStatistics.messagesOpenRate == null ? undefined : (
          <Trans
            i18nKey={ STRING_KEYS.POSITION_OVERVIEW_PAGE.POSITION_PERFORMANCE_FUNNEL.CONTACTED.INFO_TEXT }
            values={ { openRate: Math.trunc(positionStatistics.messagesOpenRate * 100) } }
          />
        ) }
        start={ createGraphPoint(contactedMatchesRatio) }
        end={ createGraphPoint(repliedConversationsRatio) }
      />
    );
  };

  const renderRepliedBar = () => {
    return (
      <StyledFunnelBar
        title={ translate(STRING_KEYS.POSITION_OVERVIEW_PAGE.POSITION_PERFORMANCE_FUNNEL.REPLIED.TITLE) }
        Icon={ RepliedBarIcon }
        count={ positionStatistics.repliedConversations }
        infoText={ positionStatistics.messagesReplyRate == null ? undefined : (
          <Trans
            i18nKey={ STRING_KEYS.POSITION_OVERVIEW_PAGE.POSITION_PERFORMANCE_FUNNEL.REPLIED.INFO_TEXT }
            values={ { replayRate: Math.trunc(positionStatistics.messagesReplyRate * 100) } }
          />
        ) }
        start={ createGraphPoint(repliedConversationsRatio) }
        end={ createGraphPoint(repliedConversationsRatio) }
      />
    );
  };

  return (
    <Container>
      { renderPeopleMatchedBar() }
      { renderLikedBar() }
      { renderContactedBar() }
      { renderRepliedBar() }
    </Container>
  );
};

export default PositionPerformanceFunnel;
